import React, { useState, useEffect, useCallback } from 'react';

interface AuditLogEntry {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  targetType: string;
  targetId: string;
  targetName?: string;
  details: Record<string, unknown>;
  result: 'success' | 'failure';
  timestamp: string;
  ipAddress?: string;
}

interface AdminAuditTrailProps {
  apiBaseUrl: string;
  adminKey: string;
}

const ACTION_COLORS: Record<string, string> = {
  'ban_account': '#ef4444',
  'unban_account': '#22c55e',
  'give_currency': '#3b82f6',
  'give_item': '#8b5cf6',
  'teleport_player': '#06b6d4',
  'reset_world': '#f59e0b',
  'claim_territory': '#fbbf24',
  'broadcast': '#a855f7',
  'toggle_bots': '#ec4899',
  'reset_economy': '#ef4444',
};

export const AdminAuditTrail: React.FC<AdminAuditTrailProps> = ({ apiBaseUrl, adminKey }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterAdmin, setFilterAdmin] = useState<string>('');
  const [filterResult, setFilterResult] = useState<string>('');

  const fetchLogs = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/audit-trail?limit=100`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(Array.isArray(data.logs) ? data.logs : []);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  useEffect(() => {
    fetchLogs();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchLogs(), 10000);
    return () => clearInterval(interval);
  }, [fetchLogs, autoRefresh]);

  const filteredLogs = logs.filter(log => {
    if (filterAction && log.action !== filterAction) return false;
    if (filterAdmin && log.adminId !== filterAdmin) return false;
    if (filterResult && log.result !== filterResult) return false;
    return true;
  });

  const uniqueActions = Array.from(new Set(logs.map(l => l.action)));
  const uniqueAdmins = Array.from(new Set(logs.map(l => l.adminId)));

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}>
        <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>ADMIN ACTION AUDIT TRAIL</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Auto-refresh
          </label>
          <button
            onClick={() => fetchLogs()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 0,
            }}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12,
        marginBottom: 12,
      }}>
        <div>
          <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
            Filter by Action
          </label>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 11,
              borderRadius: 0,
            }}
          >
            <option value="">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>
                {action.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
            Filter by Admin
          </label>
          <select
            value={filterAdmin}
            onChange={(e) => setFilterAdmin(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 11,
              borderRadius: 0,
            }}
          >
            <option value="">All Admins</option>
            {uniqueAdmins.map(adminId => {
              const adminName = logs.find(l => l.adminId === adminId)?.adminName || adminId;
              return (
                <option key={adminId} value={adminId}>
                  {adminName}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
            Filter by Result
          </label>
          <select
            value={filterResult}
            onChange={(e) => setFilterResult(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 11,
              borderRadius: 0,
            }}
          >
            <option value="">All Results</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
          </select>
        </div>
      </div>

      {/* Audit Log Table */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        maxHeight: 500,
        overflowY: 'auto',
      }}>
        {filteredLogs.length === 0 ? (
          <div style={{
            padding: 16,
            color: '#718096',
            fontSize: 12,
            textAlign: 'center',
          }}>
            No audit logs found
          </div>
        ) : (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '100px 80px 100px 120px 1fr 80px 60px',
              gap: 8,
              padding: '8px 12px',
              backgroundColor: '#2d3748',
              borderBottom: '1px solid #4a5568',
              fontSize: 10,
              color: '#a0aec0',
              fontWeight: 600,
              position: 'sticky',
              top: 0,
            }}>
              <div>Time</div>
              <div>Admin</div>
              <div>Action</div>
              <div>Target</div>
              <div>Details</div>
              <div>Result</div>
              <div>IP</div>
            </div>
            {filteredLogs.map((log, idx) => (
              <div
                key={log.id || idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 80px 100px 120px 1fr 80px 60px',
                  gap: 8,
                  padding: '8px 12px',
                  borderBottom: idx < filteredLogs.length - 1 ? '1px solid #2d3748' : 'none',
                  fontSize: 10,
                  color: '#e2e8f0',
                  alignItems: 'center',
                }}
              >
                <div style={{ color: '#a0aec0' }}>
                  {formatTime(log.timestamp)}
                </div>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.adminName}
                </div>
                <div style={{ color: ACTION_COLORS[log.action] || '#a0aec0' }}>
                  {log.action.replace(/_/g, ' ')}
                </div>
                <div style={{ color: '#fbbf24', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.targetName || log.targetId}
                </div>
                <div style={{ color: '#718096', fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {JSON.stringify(log.details).substring(0, 50)}...
                </div>
                <div style={{ color: log.result === 'success' ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                  {log.result === 'success' ? '[OK]' : '[FAIL]'}
                </div>
                <div style={{ color: '#718096', fontSize: 9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.ipAddress || '-'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{
        marginTop: 12,
        padding: 8,
        backgroundColor: '#0b1220',
        borderRadius: 4,
        fontSize: 10,
        color: '#a0aec0',
      }}>
        Showing {filteredLogs.length} of {logs.length} actions
      </div>
    </div>
  );
};
